import type { AxiosError } from '@/apis/apiErrors';
import type { ChatChartSpec } from '@/common/chat/parseChatMessageParts';
import {
  normalizeActionResult,
  normalizeAskUser,
  normalizeAskUserAnswers,
} from './chatActions';
import type {
  AIChatConversationHistoryTurn,
  AIChatFeedbackRating,
  AIChatMessage,
  AIChatMessagePart,
  AskUserRequest,
  ChatActionResultPayload,
  AIChatSession,
  AIChatSessionGroup,
  AIChatThinkingStep,
  SavedQuestion,
} from './types';
import type { HelpCenterAnswerSource } from '@/layout/AppHelpCenter/types';

export const LAST_SESSION_STORAGE_KEY =
  'zoomlogi-ai-insights-chat-last-session-id';
export const EXPANDED_VIEW_QUERY = '(min-width: 768px)';

export function customResponseErrorMessage(
  err: AxiosError
): string | undefined {
  const wrapped = err.response?.data as
    { data?: { message?: unknown } } | undefined;
  const msg = wrapped?.data?.message;
  return typeof msg === 'string' && msg.trim() ? msg : undefined;
}

function normalizeFeedbackRating(rating: unknown): AIChatFeedbackRating | null {
  return rating === 'thumbs_up' || rating === 'thumbs_down' ? rating : null;
}

export function normalizeSql(sql: unknown): string | null {
  return typeof sql === 'string' && sql.trim() ? sql : null;
}

// AgentCore turns persist a `queries` array (one per tool call this turn)
// rather than the old chatbot's single `sql` string — join them the same
// way the live-streaming path does, so history reloads render identically
// to how the turn originally streamed in.
function joinedQueriesSql(queries: unknown): string | null {
  if (!Array.isArray(queries)) return null;
  const strings = queries.filter(
    (query): query is string => typeof query === 'string'
  );
  return strings.length > 0 ? strings.join('\n\n-- \n\n') : null;
}

function historicalCharts(charts: unknown): ChatChartSpec[] {
  if (!Array.isArray(charts)) return [];
  return charts.filter(
    (c): c is ChatChartSpec =>
      Boolean(c) &&
      typeof c === 'object' &&
      Array.isArray((c as Record<string, unknown>).categories)
  );
}

function historicalActionResults(
  actionResults: unknown
): ChatActionResultPayload[] {
  if (!Array.isArray(actionResults)) return [];
  return actionResults
    .map(normalizeActionResult)
    .filter((result): result is ChatActionResultPayload => result !== null);
}

function historicalAskUser(askUser: unknown): AskUserRequest[] | undefined {
  if (!Array.isArray(askUser)) return undefined;
  const requests = askUser
    .map(normalizeAskUser)
    .filter((request): request is AskUserRequest => request !== null);
  return requests.length > 0 ? requests : undefined;
}

// Only built when the turn actually has persisted charts or action results
// -- an AgentCore turn with neither, and every turn from the older,
// non-AgentCore chatbot (which never persists either), fall through to
// parseChatMessageParts(content) instead (see AIChatThread.tsx), which
// already handles both correctly: nothing to extract for the former, a
// fenced ```chart block to extract for the latter. Charts (then action
// results) always render after the turn's full text here, not interleaved
// at their original position -- see _persist_assistant_turn's docstring
// for why.
function historicalMessageParts(
  content: string,
  charts: unknown,
  actionResults: unknown
): AIChatMessagePart[] | undefined {
  const parsedCharts = historicalCharts(charts);
  const parsedResults = historicalActionResults(actionResults);
  if (parsedCharts.length === 0 && parsedResults.length === 0) {
    return undefined;
  }
  const parts: AIChatMessagePart[] = [];
  if (content) parts.push({ kind: 'text', text: content });
  for (const spec of parsedCharts) parts.push({ kind: 'chart', spec });
  for (const result of parsedResults) {
    parts.push({ kind: 'action_result', result });
  }
  return parts;
}

// A persisted turn only ever stores a fully-finished response, so every
// step it recorded is implicitly "completed" -- there's no pending state
// to reconstruct, unlike the live-streaming path.
function historicalThinkingSteps(
  thinkingSteps: unknown
): AIChatThinkingStep[] | undefined {
  if (!Array.isArray(thinkingSteps)) return undefined;
  const names = thinkingSteps.filter(
    (name): name is string => typeof name === 'string' && name.length > 0
  );
  return names.length > 0
    ? names.map((name) => ({ name, status: 'completed' as const }))
    : undefined;
}

/** "Answered from the help center": the backend's label and article links on a
 * how-to answer, from either chat (live reply or saved turn). Undefined on a
 * normal data answer, or when nothing usable came back. */
export function toHelpSource(
  label: unknown,
  articles: unknown
): HelpCenterAnswerSource | undefined {
  if (!Array.isArray(articles)) return undefined;
  const links = articles.filter(
    (item): item is { id: string; title: string } =>
      typeof item?.id === 'string' && typeof item?.title === 'string'
  );
  if (links.length === 0) return undefined;
  return {
    label: typeof label === 'string' ? label : '',
    articles: links.map(({ id, title }) => ({ id, title })),
  };
}

export function conversationHistoryToMessages(
  history: AIChatConversationHistoryTurn[]
): AIChatMessage[] {
  if (!Array.isArray(history)) return [];
  let pendingFeedback: AIChatFeedbackRating | null = null;

  return history.map((turn) => {
    const content = String(turn.content ?? '');
    const rating = normalizeFeedbackRating(turn.rating);
    if (turn.role === 'user') {
      pendingFeedback = rating;
      const answers = normalizeAskUserAnswers(turn.answers);
      return answers
        ? { type: 'user' as const, content, answers }
        : { type: 'user' as const, content };
    }
    if (turn.role === 'assistant') {
      const feedback = rating ?? pendingFeedback;
      pendingFeedback = null;
      return {
        type: 'assistant' as const,
        content,
        parts: historicalMessageParts(
          content,
          turn.charts,
          turn.action_results
        ),
        askUser: historicalAskUser(turn.ask_user),
        feedback,
        sql: normalizeSql(turn.sql) ?? joinedQueriesSql(turn.queries),
        thinkingSteps: historicalThinkingSteps(turn.thinking_steps),
        helpSource: toHelpSource(turn.label, turn.help_articles),
      };
    }
    return { type: 'system' as const, content };
  });
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getSessionBucket(createdAt?: string): AIChatSessionGroup['label'] {
  if (!createdAt) return 'Older';

  const createdDate = new Date(createdAt);
  const createdTime = createdDate.getTime();
  if (Number.isNaN(createdTime)) return 'Older';

  const todayStart = startOfLocalDay(new Date());
  const createdDayStart = startOfLocalDay(createdDate);
  const ageInDays = Math.floor(
    (todayStart.getTime() - createdDayStart.getTime()) / 86_400_000
  );

  if (ageInDays <= 0) return 'Today';
  if (ageInDays <= 30) return 'Past 30 days';
  return 'Older';
}

export function groupSessionsByCreatedAt(
  sessions: AIChatSession[]
): AIChatSessionGroup[] {
  const groups: AIChatSessionGroup[] = [
    { label: 'Today', items: [] },
    { label: 'Past 30 days', items: [] },
    { label: 'Older', items: [] },
  ];

  sessions.forEach((session) => {
    const label = getSessionBucket(session.createdAt);
    groups.find((group) => group.label === label)?.items.push(session);
  });

  return groups.filter((group) => group.items.length > 0);
}

function unwrapApiData(payload: unknown): unknown {
  let data: unknown = payload;

  for (let depth = 0; depth < 4; depth += 1) {
    if (data == null || typeof data !== 'object') break;
    const current = data as Record<string, unknown>;
    if (Array.isArray(current.sessions)) return current;
    if (!('data' in current)) break;
    data = current.data;
  }

  return data;
}

export function getSessionsPageCountFromPayload(payload: unknown): number {
  let data: unknown = payload;

  for (let depth = 0; depth < 4; depth += 1) {
    if (data == null || typeof data !== 'object') break;
    const current = data as Record<string, unknown>;
    if (typeof current.page_count === 'number') return current.page_count;
    if (typeof current.page_count === 'string') {
      const parsed = Number(current.page_count);
      if (Number.isFinite(parsed)) return parsed;
    }
    if (!('data' in current)) break;
    data = current.data;
  }

  return 1;
}

export function getSessionsFromPayload(payload: unknown): AIChatSession[] {
  const data = unwrapApiData(payload);
  const sessions =
    data != null && typeof data === 'object'
      ? (data as Record<string, unknown>).sessions
      : undefined;

  if (!Array.isArray(sessions)) return [];

  return sessions
    .map((row): AIChatSession | null => {
      if (row == null || typeof row !== 'object') return null;
      const item = row as Record<string, unknown>;
      const rawId = item.session_id ?? item.id;
      const rawTitle = item.title;
      const rawCreatedAt = item.created_at ?? item.createdAt;
      const id = typeof rawId === 'string' ? rawId : '';
      if (!id) return null;

      return {
        id,
        title:
          typeof rawTitle === 'string' && rawTitle.trim()
            ? rawTitle.trim()
            : 'Chat',
        createdAt: typeof rawCreatedAt === 'string' ? rawCreatedAt : undefined,
        messages: [] as AIChatMessage[],
      };
    })
    .filter((session): session is AIChatSession => session !== null)
    .sort((a, b) => {
      const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
      const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
      return bTime - aTime;
    });
}

export function appendTypingIfPending(
  messages: AIChatMessage[],
  sessionId: string,
  pendingAssistantBySession: ReadonlySet<string>
): AIChatMessage[] {
  if (!pendingAssistantBySession.has(sessionId)) return messages;
  // The AgentCore path now seeds a real in-progress assistant message (see
  // sendMessage's `render()`) instead of relying solely on this placeholder
  // -- checking for `streaming` too, not just the literal 'typing' message,
  // stops this from appending a redundant second indicator on top of it.
  const hasIndicator = messages.some(
    (message) => message.content === 'typing' || message.streaming
  );
  if (hasIndicator) return messages;
  return [...messages, { type: 'system', content: 'typing' }];
}

export function getStoredLastSessionId(): string | null {
  try {
    return window.sessionStorage.getItem(LAST_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeLastSessionId(sessionId: string | null): void {
  if (!sessionId) return;
  try {
    window.sessionStorage.setItem(LAST_SESSION_STORAGE_KEY, sessionId);
  } catch {
    // Best-effort UX persistence only.
  }
}

export function clearStoredLastSessionId(): void {
  try {
    window.sessionStorage.removeItem(LAST_SESSION_STORAGE_KEY);
  } catch {
    // Best-effort UX persistence only.
  }
}

function normalizeSavedQuestion(row: unknown): SavedQuestion | null {
  if (row == null || typeof row !== 'object') return null;
  const item = row as Record<string, unknown>;
  const rawId = item.id;
  const rawQuestion = item.question;
  const rawCreatedAt = item.created_at;
  const rawUpdatedAt = item.updated_at;
  const id = typeof rawId === 'string' ? rawId : '';
  const question = typeof rawQuestion === 'string' ? rawQuestion.trim() : '';
  if (!id || !question) return null;
  return {
    id,
    question,
    createdAt: typeof rawCreatedAt === 'string' ? rawCreatedAt : undefined,
    updatedAt: typeof rawUpdatedAt === 'string' ? rawUpdatedAt : undefined,
  };
}

export function getSavedQuestionsFromPayload(
  payload: unknown
): SavedQuestion[] {
  let data: unknown = payload;

  for (let depth = 0; depth < 4; depth += 1) {
    if (data == null || typeof data !== 'object') break;

    const current = data as Record<string, unknown>;
    if (Array.isArray(current.saved_questions) || Array.isArray(data)) {
      break;
    }

    data = current.data;
  }

  const container = data as
    | {
        saved_questions?: unknown;
      }
    | undefined;
  const rows = container?.saved_questions ?? (Array.isArray(data) ? data : []);

  return Array.isArray(rows)
    ? rows
        .map(normalizeSavedQuestion)
        .filter((item): item is SavedQuestion => Boolean(item))
    : [];
}

export function chunkItems<T>(items: T[]): T[][] {
  return items.reduce<T[][]>((rows, question, index) => {
    if (index % 2 === 0) rows.push([]);
    rows[rows.length - 1].push(question);
    return rows;
  }, []);
}

export function chunkCollapsedSavedQuestions(
  questions: SavedQuestion[]
): SavedQuestion[][] {
  const rowWidth = 388;
  const gap = 4;
  const fixedChipWidth = 54;
  const averageCharacterWidth = 8;
  const estimateChipWidth = (question: SavedQuestion) =>
    fixedChipWidth + question.question.trim().length * averageCharacterWidth;
  const rows: SavedQuestion[][] = [];
  let currentRow: SavedQuestion[] = [];
  let currentRowWidth = 0;

  questions.forEach((question) => {
    const chipWidth = estimateChipWidth(question);
    const isLongQuestion = chipWidth > rowWidth;

    if (isLongQuestion) {
      if (currentRow.length > 0) {
        rows.push(currentRow);
        currentRow = [];
        currentRowWidth = 0;
      }
      rows.push([question]);
      return;
    }

    const nextRowWidth =
      currentRow.length === 0 ? chipWidth : currentRowWidth + gap + chipWidth;

    if (currentRow.length > 0 && nextRowWidth > rowWidth) {
      rows.push(currentRow);
      currentRow = [];
      currentRowWidth = 0;
    }

    currentRow.push(question);
    currentRowWidth =
      currentRowWidth === 0 ? chipWidth : currentRowWidth + gap + chipWidth;
  });

  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  return rows;
}
